import React, { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from "react-dom"
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Menu,
    MenuItem,
    Typography,
    Popper,
    ClickAwayListener,
    Paper,
    TextField,
} from "@mui/material"
import PushPinIcon from "@mui/icons-material/PushPin"
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined"
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread"
import MarkChatReadIcon from "@mui/icons-material/MarkChatRead"
import { FixedSizeList } from 'react-window';
import { getSession } from "../../../auth"

import useNotification from "../../Notification"
import ConversationItem from '../../Chat/ConversationList/ConversationItem';
import AutoSizer from "react-virtualized-auto-sizer"
import Skeleton from '@mui/material/Skeleton';
import HangupDialog from "../../Dialogs/Hangup"
import TransferDialog from "../../Dialogs/Transfer"
import SettingsIcon from "@mui/icons-material/Settings"
import NotificationsIcon from "@mui/icons-material/Notifications"
import api from "../../../api"
import closeAllConversations from "../../CortexAssistant/closeAllConversations"

const buildNotificationMessage = (notification) => {
    let template = JSON.parse(notification.template)
    let finalMessage = template.components.find(component => component.type === 'BODY').text
    let initialMessage = null
    let response = []
    let headerFormat =  template.components.find(component => component.type === 'HEADER')?.format

    if (notification.parameters !== null) {
        let parameters = JSON.parse(notification.parameters);
        if (typeof parameters === 'string' || parameters instanceof String)
            // Reparse JSON so it works
            parameters = JSON.parse(parameters);

        let bodyparameters = parameters.find(each => each.body)
        if (bodyparameters?.body?.length > 0) {
            bodyparameters = bodyparameters.body;
        }

        bodyparameters?.forEach((parameter) => {
            finalMessage = finalMessage.replace(/\{\{\d*\}\}/, parameter)
        })

        let parametersHead = parameters.find(parameter => parameter.headerfile)
        if (parametersHead) {
            switch(headerFormat){
                case 'IMAGE':
                    initialMessage = `[img]${parametersHead.headerfile}[/img]`
                    break
                case 'VIDEO':
                    initialMessage = `[video]${parametersHead.headerfile}[/video]`
                    break
                case 'DOCUMENT':
                    initialMessage = `[url=${parametersHead.headerfile}][/url]`
                    break
                default:
                    initialMessage = parametersHead.headerbody
            }
            
        }

    }
    
    let time = new Date(notification.processed_at)
    let final_time = (new Date(time - 3 * 60 * 60 * 1000)).toLocaleTimeString(
        [], { hour: '2-digit', minute: '2-digit', hour12: false })


    if (initialMessage)
        response.push(
            {
                message: initialMessage,
                user_id: -1,
                is_read: true,
                iso_time: final_time
            }
        )
    if (finalMessage)
        response.push(
            {
                message: finalMessage,
                user_id: -1,
                is_read: true,
                iso_time: final_time
            }
        )
    return response
}

const styles = {
    row: {
        width: '100%',
        backgroundColor: '',
        maxHeight: '65vh',
        overflow: 'auto',
        padding: 0,
        position: '',
        overflow: 'auto'
    },
};

const QueueContainer = ({
    queueName,
    queueIcon,
    queueWhere,
    queueAmount,
    queueRow,
    onClickItem,
    fetching,
    limit,
    callData,
    queueAdded,
    listMode,
    isManageCalls,
    setReadMessages,
    activeChat,
    AsNotification,
    labels,
    connectEmailChat,
    sortComponent,
    regenerate,
    progressBarMax = undefined,
    version="service",
    showEscalation=()=>{},
    setProgressBarMax=()=>{},
    onPinToggle=null,
    onMarkUnread=null,
}) => {

    const [loading, setLoading] = useState(false)
    const [loaded, setLoaded] = useState([])
    const [thisQueue, setThisQueue] = useState([])
    const [updating, setUpdating] = useState(false)
    const [offset, setOffset] = useState(0)

    const [hangupChat, setHangupChat] = useState(null)
    const [transferError, setTransferError] = useState(null)
    const [transferChat, setTransferChat] = useState(null)

    const draggingIndexRef = useRef(null)
    const dragStartRef = useRef({ x: 0, y: 0 })
    const dragActiveRef = useRef(false)
    const DRAG_THRESHOLD = 6

    const [draggingIndex, setDraggingIndex] = useState(null)
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
    const [dragSize, setDragSize] = useState({ width: 0, height: 0 })

    const [ progressBarKey, setProgressBarKey ] = useState(0)
    const [ settingsAnchorEl, setSettingsAnchorEl] = useState(null)
    const [contextMenu, setContextMenu] = useState({ chat: null, mouseX: null, mouseY: null })

    useEffect(() => {
        const timer = setTimeout(() => setProgressBarKey(progressBarMax), 1000)
        return () => clearTimeout(timer)
    }, [progressBarMax])

    useEffect(()=>{
        if (regenerate)
            regenerate()
    }, [])

    useEffect(() => {
        if (queueRow.length !== 0){
            setThisQueue(queueRow)
        }
    }, [queueRow])

    const updateData = async (newOffset) => {
        if (loaded.includes(offset) || updating) return

        setUpdating(true)
        const signal = await callData(queueWhere, newOffset)
        if (signal) {
            setLoaded([...loaded, newOffset])
        }
        setUpdating(false)
    }

    useEffect(() => {
        updateData(offset)
    }, [offset])

    useEffect(() => {
        setLoaded([])
    }, [queueAdded])


    if (!updating && offset >= 0 && !loaded.includes(offset)) {
        updateData(offset)
    }

    const handleDrop = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const column = el?.closest("[data-drop-column]")
      document
        .querySelectorAll("[data-drop-column]")
        .forEach(c => c.classList.remove("drop-hover"))
      if (!column) {
        draggingIndexRef.current = null
        setDraggingIndex(null)
        return
      }
      const targetColumn = column.dataset.dropColumn
      const draggedChat = thisQueue.find(c => c.position === draggingIndexRef.current)
      if (targetColumn === "finished") 
        setHangupChat(draggedChat)
      else if (targetColumn === "queue")
        setTransferChat(draggedChat)
      draggingIndexRef.current = null
      setDraggingIndex(null)
    }

    const handlePointerMove = (ev) => {
      setDragPosition({ x: ev.clientX, y: ev.clientY })
      const el = document.elementFromPoint(ev.clientX, ev.clientY)
      const column = el?.closest("[data-drop-column]")
      document
        .querySelectorAll("[data-drop-column]")
        .forEach(c => c.classList.remove("drop-hover"))
      if (column) column.classList.add("drop-hover")
    }

    const renderListItem = (props) => {
        const { index, style } = props
        
        let difference = Math.abs(index % limit)
        if (!updating && difference === 0 && !loaded.includes(index))
            setOffset(index)

        let chat = thisQueue.find(item => item.position === index)
        if (chat)
            chat.where = queueWhere

        if (!chat) {
            return (
                <ListItem style={style} key={index} component="div">
                    <Box key={`${index}-key-on-queue`} bgcolor='' sx={{ width: "100%", height: "100%" }}>
                        <Skeleton sx={{ height: "100%" }} variant="rectangular" animation="wave" />
                        <Divider />
                    </Box>
                </ListItem>
            )
        }

        return (
            <ListItem
                style={style}
                key={`${chat.uniqueid}-key-list-${queueName}`}
                component="div"
                disablePadding={(version) !== "manageCalls" ? true : false}
                sx={{ padding: (version) !== "manageCalls" ? 0 : "4px 8px" }}
                onContextMenu={onPinToggle ? (e) => { e.preventDefault(); setContextMenu({ chat, mouseX: e.clientX, mouseY: e.clientY }) } : undefined}
            >
                <ListItemButton sx={{ padding: 0, "&:hover": { backgroundColor: (version === "manageCalls") ? "transparent" : "#f2f4f6" }}}>
                    <Box key={`${chat.uniqueid}-key-on-${queueName}`} sx={{ width: "100%", height: "100%" }}>
                        <ConversationItem
                            onPointerDown={(e) => {
                                const cardEl = e.target.closest(".custom-card")
                                if (!cardEl) return

                                dragStartRef.current = { x: e.clientX, y: e.clientY }
                                dragActiveRef.current = false
                                draggingIndexRef.current = index

                                const rect = cardEl.getBoundingClientRect()
                                setDragSize({
                                    width: rect.width,
                                    height: rect.height,
                                })

                                const move = (ev) => {
                                    const dx = Math.abs(ev.clientX - dragStartRef.current.x)
                                    const dy = Math.abs(ev.clientY - dragStartRef.current.y)

                                    // Only activate drag after threshold
                                    if (!dragActiveRef.current && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
                                        dragActiveRef.current = true
                                        setDraggingIndex(index)
                                    }

                                    if (dragActiveRef.current) {
                                        handlePointerMove(ev)
                                    }
                                }

                                const up = (ev) => {
                                    if (dragActiveRef.current) {
                                        handleDrop(ev)
                                    }
                                    window.removeEventListener("pointermove", move)
                                    window.removeEventListener("pointerup", up)
                                    dragActiveRef.current = false
                                    draggingIndexRef.current = null
                                    setDraggingIndex(null)
                                }

                                window.addEventListener("pointermove", move)
                                window.addEventListener("pointerup", up)
                            }}
                            onClick={() => { onClickItem(chat); }}
                            progressBarMax={progressBarMax}
                            activeId={activeChat?.uniqueid}
                            conversation={chat}
                            isManageCalls={isManageCalls ? true : false}
                            setReadMessages={!isManageCalls ? setReadMessages : () => { }}
                            labels={labels}
                            connectEmailChat={connectEmailChat}
                            version={version}
                        />
                    </Box>
                </ListItemButton >
            </ListItem>
        )
    }

    const renderList = useMemo(() => {
        if (fetching)
            return <Box id="conversationList-box-4"><CircularProgress /></Box>
        return (
            <AutoSizer>
                {({ height, width }) => (
                    <FixedSizeList height={height} width={width} itemSize={(version === "manageCalls") ? 114 : 100} itemCount={queueAmount} overscanCount={5}>
                        {renderListItem}
                    </FixedSizeList>
                )}
            </AutoSizer>
        )
    }, [updating, fetching, activeChat, progressBarKey, thisQueue])

    const finishChat = async (extraData = [], message = null) => {
      const agentId = getSession().profile?.agent_id
      const agentName = getSession().profile?.agent?.name
      const settings = getSession().profile?.enterprise?.settings

      const body = {
        channel   : hangupChat.channel ?? hangupChat.hash,
        unique_id : hangupChat.uniqueid,
        callerid  : hangupChat.callerid
      }

      if (settings?.agent_identification && settings?.isAgentIdentificated)
        body["agentName"] = settings.isAgentIdentificated
      if (extraData.length)
        body["extra_data"] = extraData
      if (message)
        body["message"] = message

      const isBotChat = hangupChat.chatstatus === null
      const isQueueChat = hangupChat.chatstatus == 'QUEUE'

      let url = `api/IV/agents/${agentId}/hangup/`
      if (isBotChat || isQueueChat || message !== null)
        url = `api/IV/agents/${agentId}/bot_hangup/`

      setLoading(true)
      const response = await api.post(url, body)
      setLoading(false)
      if (response.status == 200 || response.status == 204) {
        const socket = new WebSocket(`wss://${window.location.hostname}/chat/ws/chat/${hangupChat.channel ?? hangupChat.hash}/`)
        socket.onopen = () => {
          const userId = isBotChat ? -7 : -5
          socket.send(
            JSON.stringify({
              channel: hangupChat.channel ?? hangupChat.hash,
              message: JSON.stringify({ chatID: hangupChat.id, actor: agentName }),
              user: userId
            })
          )
          socket.close()
        }
        setThisQueue((prev) => prev.filter((item) => item.id !== hangupChat.id))
        setHangupChat(null)
        closeAllConversations(hangupChat.uniqueid)
      } else if (response.status == 503) {
        useNotification(
          "Manutenção!",
          "Estamos em manutenção, tente novamente mais tarde.",
          "warning"
        )
      } else if (response.status == 400 && response.data === "You are not allowed to hangup chats.") {
        useNotification(
          "Atenção!",
          "Como agente de suporte, você não pode desligar atendimentos.",
          "warning"
        )
      } else {
        useNotification(
          "Ops!",
          "Algo deu errado, tente novamente em alguns instantes.",
          "danger"
        )
      }
    }

    const transfer = async (queueName, transferNote = "", queueDescription = null, forced = false) => {
      const agentId = getSession().profile?.agent_id
      const agentName = getSession().profile?.agent?.name
      const queues = getSession().profile?.enterprise?.queues
      const isBotChat = transferChat.chatstatus === null

      let url = `api/IV/agents/${agentId}/transfer/?forced=${forced}`
      if (isBotChat)
          url = `api/IV/agents/${agentId}/bot_transfer/?forced=${forced}`

      setLoading(true)
      const response = await api.post(url, {
        queue: queueName,
        unique_id: transferChat.uniqueid,
        transfer_note: transferNote
      })
      setLoading(false)

      if (response.status == 409) {
        useNotification(
          "Atenção!",
          "Não é possível transferir esse atendimento para nenhuma fila.",
          "warning"
        )
        return
      }

      if (response.status == 400 && response.data === "You are not allowed to transfer chats.") {
        useNotification(
          "Atenção!",
          "Como agente de suporte, você não pode transferir atendimentos.",
          "warning"
        )
        return
      }

      if (isBotChat) {
        if (response.status === 400) {
          useNotification(
            "Ops!",
            `O comando de forçar transferência não está configurado em seu BOT. Entre em contato com o suporte para utilizá-lo.`,
            "warning"
          )
          return
        }
      } 

      if (response.status === 400 && response.data?.includes("Não há agentes logados na fila")) {
        useNotification(
          "Atenção!",
          "Esta fila não aceita atendimentos sem agentes logados.",
          "warning"
        )
        return
      }

      if (response.status === 200) {
        const socket = new WebSocket(`wss://${window.location.hostname}/chat/ws/chat/${transferChat.channel ?? transferChat.hash}/`)
        socket.onopen = () => {
          socket.send(
            JSON.stringify({
              message: "DROP_CHAT",
              channel: { channel: transferChat.channel ?? transferChat.hash, user: agentName }
            })
          )
          const queueDescAlt = queues?.find(queue => queue.name === queueName)?.description
          const queueAlt = queues?.find(queue => queue.name === transferChat.queue)?.description
          const actor = getSession().profile?.nickname || getSession().first_name
          socket.send(
            JSON.stringify({
              "channel": transferChat.channel ?? transferChat.hash,
              "message": JSON.stringify({
                chatID: transferChat.id,
                actor: actor,
                queue: queueDescription || queueDescAlt || "Desconhecida",
                last_queue: transferChat?.queue?.description || queueAlt || "Bot",
              }),
              "user": -4
            })
          )
          socket.close()
        }
        setThisQueue((prev) => prev.filter((item) => item.id !== transferChat.id))
        setTransferChat(null)
      } else {
        useNotification(
          "Ops!",
          "Algo deu errado, tente novamente em alguns instantes.",
          "danger"
        )
      }
    }

    return (
        <>
            {hangupChat &&
              createPortal(
                <HangupDialog open={true}
                  title="Encerrar atendimento"
                  activeChat={hangupChat}
                  description="Tem certeza que deseja finalizar esse atendimento?"
                  handleClose={() => setHangupChat(null)}
                  handleSubmit={finishChat}
                  submitButtonLoading={loading}
                />,
                document.body
              )
            }
            {transferChat &&
              createPortal(
                <TransferDialog open={true}
                  title="Transferir atendimento"
                  enterpriseId={getSession().profile?.enterprise?.id}
                  queues={[]}
                  queueId={transferChat.queue?.id}
                  queueName={transferChat.queue?.description}
                  transferChat={transfer}
                  error={transferError}
                  handleClose={() => setTransferChat(null)}
                  submitButtonLoading={loading}
                />,
                document.body
              )
            }
            {draggingIndex !== null &&
                createPortal(
                    <Box
                        sx={{
                            position: "fixed",
                            top: dragPosition.y,
                            left: dragPosition.x,
                            transform: "translate(-50%, -50%) scale(1.02)",
                            pointerEvents: "none",
                            zIndex: 9999,
                            width: dragSize.width,
                            height: dragSize.height,
                            opacity: 0.95,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                            transition: "transform 0.05s linear",
                        }}
                    >
                        <ConversationItem
                            conversation={thisQueue.find(c => c.position === draggingIndex)}
                            version={version}
                        />
                    </Box>,
                    document.body
                )
            }
            {onPinToggle &&
                <Menu
                    open={Boolean(contextMenu.chat)}
                    onClose={() => setContextMenu({ chat: null, mouseX: null, mouseY: null })}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu.mouseY !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined}
                >
                    <MenuItem onClick={() => {
                        onPinToggle(contextMenu.chat, queueWhere)
                        setContextMenu({ chat: null, mouseX: null, mouseY: null })
                    }} sx={{ gap: 1 }}>
                        {contextMenu.chat?.is_pinned
                            ? <><PushPinOutlinedIcon fontSize="small" /> Desfixar</>
                            : <><PushPinIcon fontSize="small" /> Fixar</>
                        }
                    </MenuItem>
                    {onMarkUnread && (
                        <MenuItem onClick={() => {
                            onMarkUnread(contextMenu.chat, queueWhere)
                            setContextMenu({ chat: null, mouseX: null, mouseY: null })
                        }} sx={{ gap: 1 }}>
                            {contextMenu.chat?.is_marked_unread
                                ? <><MarkChatReadIcon fontSize="small" /> Marcar como lido</>
                                : <><MarkChatUnreadIcon fontSize="small" /> Marcar como não lido</>
                            }
                        </MenuItem>
                    )}
                </Menu>
            }
            {listMode
                ? renderList
                : <Grid item lg={3} md={3} sm={3} xs={3} sx={{ minWidth: '25rem' }}>
                    <Box className="call-columns-v2 queue-chats">
                        <Box className="column-header-v2">
                            <Box className="column-title-v2">
                                {queueIcon}
                                <strong>{queueName}</strong>
                                {queueAmount > 0 && <Chip label={queueAmount} size="small" color="primary" />}
                                {/*{sortComponent}*/}
                            </Box>
                            <IconButton className="column-action-right" onClick={() => showEscalation()}>
                                <SettingsIcon />
                            </IconButton>
                            <IconButton className="column-action-left" onClick={(event) => setSettingsAnchorEl(settingsAnchorEl ? null : event.currentTarget)}>
                                <NotificationsIcon />
                            </IconButton>
                            <Popper className="manage-calls-settings" open={Boolean(settingsAnchorEl)} anchorEl={settingsAnchorEl} placement="bottom">
                                <ClickAwayListener onClickAway={() => setSettingsAnchorEl(null)}>
                                    <Paper>
                                        <Typography>
                                            Alerta Visual
                                        </Typography>
                                        <TextField
                                            label="Minutos"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={progressBarMax}
                                            onChange={(event) => setProgressBarMax(parseInt(event.target.value))}
                                        />
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </Box>
                        <Box className={'fit-tab'}>
                            {renderList}
                        </Box>
                    </Box>
                </Grid >
            }
        </>
    )
}

export default QueueContainer